'use client';

import { useMatchMedia } from '@/utils/hooks/use-match-media';
import {
	type FC,
	useMemo,
	useContext,
	createContext,
	type PropsWithChildren,
} from 'react';

interface LeftRailContextType {
	hideLeftRail: boolean;
	isLeftRailVisible: boolean;
	leftRailContent: string;
}

export const LeftRailContext = createContext<LeftRailContextType | undefined>(
	undefined,
);

interface LeftRailContextProviderProps extends PropsWithChildren {
	hideLeftRail?: boolean;
	leftRailContent: string;
}

export const LeftRailContextProvider: FC<LeftRailContextProviderProps> = ({
	children,
	hideLeftRail = false,
	leftRailContent,
}) => {
	const [isMobile] = useMatchMedia(['(max-width: 1024px)']);

	const isLeftRailVisible = !!leftRailContent && !isMobile;

	const contextValue = useMemo(
		() => ({
			hideLeftRail,
			isLeftRailVisible,
			leftRailContent,
		}),
		[hideLeftRail, leftRailContent, isLeftRailVisible],
	);

	return (
		<LeftRailContext.Provider value={contextValue}>
			{children}
		</LeftRailContext.Provider>
	);
};

export const useLeftRailContext = () => {
	const context = useContext(LeftRailContext);

	if (!context) {
		throw new Error(
			'useLeftRailContext must be used within a LeftRailContextProvider',
		);
	}

	return context;
};
